/**
 * useMarkSeen Hook (Batch-on-Leave - Cost Optimized)
 * 
 * Tracks seen items in memory and syncs to Firestore only when user
 * leaves the page or switches tabs. ~80% cost savings vs interval-based.
 */
import { useRef, useCallback, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const MAX_BATCH_SIZE = 50;
const LOCAL_STORAGE_KEY = 'pending_seen_items';

export const useMarkSeen = () => {
    const pendingRef = useRef(new Map());  // itemId -> itemType
    const isFlushing = useRef(false);

    // Restore pending items from localStorage on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (saved) {
                const items = JSON.parse(saved);
                items.forEach(([id, type]) => pendingRef.current.set(id, type));
                localStorage.removeItem(LOCAL_STORAGE_KEY);
            }
        } catch (e) {
            console.warn('[useMarkSeen] Failed to restore pending items:', e);
        }
    }, []);

    const flushBatch = useCallback(async () => {
        if (pendingRef.current.size === 0 || isFlushing.current) return;
        isFlushing.current = true;

        const items = Array.from(pendingRef.current.entries()).slice(0, MAX_BATCH_SIZE);
        const itemIds = items.map(([id]) => id);
        const itemTypes = items.map(([, type]) => type);

        // Don't clear items until success (bug fix: was clearing before try block)

        try {
            const functions = getFunctions();
            const markSeen = httpsCallable(functions, 'markPostsSeen');
            await markSeen({ itemIds, itemTypes });

            // Only clear on SUCCESS
            items.forEach(([id]) => pendingRef.current.delete(id));

            // If more items remain, schedule next batch
            if (pendingRef.current.size > 0) {
                setTimeout(flushBatch, 100);
            }
        } catch (error) {
            console.error('[useMarkSeen] Failed, saving for retry:', error);
            // Items are still in pendingRef (we didn't delete them)
            // Save ALL pending items to localStorage for retry on next session
            const allPending = Array.from(pendingRef.current.entries());
            if (allPending.length > 0) {
                try {
                    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(allPending));
                } catch (e) {
                    console.warn('[useMarkSeen] Failed to save to localStorage:', e);
                }
            }
        } finally {
            isFlushing.current = false;
        }
    }, []);

    // Flush on visibility change (tab hidden) or page unload
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flushBatch();
            } else if (document.visibilityState === 'visible') {
                // Retry pending items when tab becomes active again
                if (pendingRef.current.size > 0) {
                    flushBatch();
                }
            }
        };

        const handleBeforeUnload = () => {
            // Save to localStorage since async calls may not complete
            if (pendingRef.current.size > 0) {
                const items = Array.from(pendingRef.current.entries());
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
            }
            flushBatch();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            flushBatch(); // Final flush on unmount
        };
    }, [flushBatch]);

    /**
     * Mark an item as seen (instant, memory only)
     * @param {string} itemId - The post or project ID
     * @param {string} itemType - 'post' or 'project'
     */
    const markAsSeen = useCallback((itemId, itemType = 'post') => {
        // Validate itemId
        if (!itemId || typeof itemId !== 'string') return;
        if (itemId.length > 100 || !/^[a-zA-Z0-9_-]+$/.test(itemId)) return;
        // Validate itemType
        if (!['post', 'project'].includes(itemType)) return;

        pendingRef.current.set(itemId, itemType);
    }, []);

    /**
     * Get count of pending items (for debugging)
     */
    const getPendingCount = useCallback(() => pendingRef.current.size, []);

    return { markAsSeen, flushBatch, getPendingCount };
};
